import { combineReducers, configureStore } from '@reduxjs/toolkit'

import { reducer as formReducer } from 'redux-form'
import { createMemoryHistory, createBrowserHistory, History } from 'history'
import { connectRouter, routerMiddleware } from 'connected-react-router'
import promise from 'redux-promise-middleware'

import * as reducers from '../reducers/all'
import myListingFormCompletion from '../dashboard/MyListings/Edit/formCompletionReduxSlice'
import selectEscapeReducer from '../components/SelectEscape/reduxSlice'
import networkMiddleware from './networkMiddleware'

const isServer = !(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
)

export type CreateHistoryOptions =
  | Parameters<typeof createMemoryHistory>[0]
  | Parameters<typeof createBrowserHistory>[0]

const createHistory = (
  options: CreateHistoryOptions,
  forceMemoryHistory?: boolean
) =>
  isServer || forceMemoryHistory
    ? createMemoryHistory(options)
    : createBrowserHistory(options)

const composeReducer = (history: History<unknown>) => {
  return combineReducers({
    ...reducers,
    myListingFormCompletion,
    selectEscapeReducer,
    router: connectRouter(history),
    form: formReducer,
  })
}

type ConfigureOptions = {
  skipImmutableCheck?: boolean
  skipSerializableCheck?: boolean
  forceMemoryHistory?: boolean
}

const configure = (
  preloadedState = {},
  createHistoryOptions: CreateHistoryOptions = { initialEntries: ['/'] },
  options?: ConfigureOptions
) => {
  const history = createHistory(
    createHistoryOptions,
    options?.forceMemoryHistory
  )

  const reducer = composeReducer(history)

  return {
    store: configureStore({
      reducer,
      middleware: (getDefaultMiddleware) =>
        // using methods on the object returned by getDefaultMiddleware maintains TS typings for middleware
        getDefaultMiddleware({
          serializableCheck:
            options?.skipSerializableCheck != null
              ? options?.skipSerializableCheck !== true
              : {
                  // We use redux-promise-middleware to wrap AJAX requests, and write the payload to the redux store.
                  // The library used to make these requests adds properties to the payload object that get flagged as
                  // 'unserializable' by the serializability dev check middleware (included by default):
                  // https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
                  ignoredActionPaths: ['payload.config', 'payload.request'],
                },
          immutableCheck: options?.skipImmutableCheck !== true,
        }).prepend(promise, routerMiddleware(history), networkMiddleware),
      preloadedState,
    }),
    history,
  }
}

export type RootState = ReturnType<ReturnType<typeof composeReducer>>

export default configure
